/**
 * 节点自定义数据访问层。
 *
 * 这里集中处理 yCustomNodeDatas 的读取、Yjs 官方 toJSON 转普通对象、以及数据变化订阅。
 * 上层模块不需要直接关心 Y.Text / Y.Array / Y.Map 的存储细节。
 */
import * as Y from 'yjs'
import { yCustomNodeDatas } from '@/shared/config/yjs-config'
import type { BaseNodeData } from '@/shared/lib/node-registry'

export interface NodeCustomDataPathSubscription {
  nodeId: string
  paths: readonly string[]
}

/**
 * 读取一个 Y.Map 的普通对象快照。
 *
 * Yjs 官方 toJSON 会递归转换 Y.Text / Y.Array / Y.Map 等共享类型。
 * 这个函数只做只读转换，不会修改 Yjs 数据。
 */
export function readYMapSnapshot(yMap: Y.Map<unknown>): Record<string, unknown> {
  return yMap.toJSON() as Record<string, unknown>
}

/**
 * 按 nodeId 读取节点自定义数据快照。
 *
 * 如果节点还没有 custom data，返回空对象，交给调用方决定如何兜底。
 */
export function readNodeCustomDataSnapshot<T extends BaseNodeData>(nodeId: string): T {
  const yNodeDataMap = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
  return yNodeDataMap ? (readYMapSnapshot(yNodeDataMap) as T) : {} as T
}

/**
 * 订阅指定节点的自定义数据变化。
 *
 * 用于上下文派生数据刷新。只要 nodeIds 中任一节点的 custom data 变化，
 * 就触发 onStoreChange；无关节点变化会被忽略。
 */
export function subscribeNodeCustomDataChanges(
  nodeIds: string[],
  onStoreChange: () => void,
) {
  if (nodeIds.length === 0) return () => { }

  const unsubs: (() => void)[] = []

  nodeIds.forEach((nodeId) => {
    const yNodeMap = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (yNodeMap) {
      const observer = (_events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
        onStoreChange()
      }
      yNodeMap.observeDeep(observer)
      unsubs.push(() => yNodeMap.unobserveDeep(observer))
    }
  })

  return () => {
    unsubs.forEach((unsub) => unsub())
  }
}

export function isObservedCustomDataPathMatched(
  changedPath: string,
  watchPaths: readonly string[],
) {
  return watchPaths.some((watchPath) => (
    changedPath === watchPath ||
    watchPath.startsWith(`${changedPath}.`) ||
    changedPath.startsWith(`${watchPath}.`)
  ))
}

function getChangedPath(
  event: { path: unknown[] },
  key?: string,
) {
  const path = [...event.path, ...(key ? [key] : [])]
  return path
    .filter((item): item is string | number => (
      typeof item === 'string' || typeof item === 'number'
    ))
    .join('.')
}

function hasWatchedPathChange(
  events: Y.YEvent<Y.AbstractType<unknown>>[],
  watchPaths: readonly string[],
) {
  return events.some((event) => {
    if (event instanceof Y.YMapEvent) {
      for (const key of event.keysChanged) {
        if (isObservedCustomDataPathMatched(getChangedPath(event, key), watchPaths)) {
          return true
        }
      }
      return false
    }

    return isObservedCustomDataPathMatched(getChangedPath(event), watchPaths)
  })
}

export function subscribeNodeCustomDataPathChanges(
  subscriptions: NodeCustomDataPathSubscription[],
  onStoreChange: () => void,
) {
  const activeSubscriptions = subscriptions.filter(({ paths }) => paths.length > 0)
  if (activeSubscriptions.length === 0) return () => { }

  const unsubs: (() => void)[] = []

  activeSubscriptions.forEach(({ nodeId, paths }) => {
    const yNodeMap = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeMap) return

    const observer = (events: Y.YEvent<Y.AbstractType<unknown>>[]) => {
      if (hasWatchedPathChange(events, paths)) {
        onStoreChange()
      }
    }
    yNodeMap.observeDeep(observer)
    unsubs.push(() => yNodeMap.unobserveDeep(observer))
  })

  return () => {
    unsubs.forEach((unsub) => unsub())
  }
}
